import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {CpConfigChildTab} from '../../../../../interface/cp-config-child-tab.interface';
import {
    CpProvisionTypeComponentOption,
    CpProvisionTypeLookupService
} from '../../../../../service/ql/cp-provision-type-lookup.service';

/**
 * navItem "Komponenten-Eignung" of the ProvisionType object component — the configuration surface
 * of K-18 `cp.ProvisionTypeComponentEligibility` (01-requirement.md §6).
 *
 * The table itself is the generic {@link CpProvisionTypeChildTableComponent} on
 * `cp.ProvisionTypeComponentEligibility`: same descriptor mechanism, same FK pre-fill, same fixed
 * column block and the same read-only condition preview as the sibling assignment tables, because
 * K-18 references a condition set exactly like they do and is matched by the identical
 * `ProvisionAssignmentMatchingService`. This component adds exactly ONE thing on top, and for exactly the reason the siblings
 * {@link CpProvisionTypeMatrixAssignmentComponent} (K-10) and
 * {@link CpProvisionTypeCalculatedAgentComponent} (K-17) add it: the pre-filter on the commission
 * component.
 *
 * ## Why the pre-filter belongs here
 * K-18 carries a MANDATORY `provisionTypeComponent` FK (01-requirement.md §6, "Zusätzliche
 * Fremdschlüssel"), so the rule set of a provision type with three components is three interleaved
 * rule sets in one table. Reading the priority order of a single component out of that mixture is
 * what the pre-filter fixes; it is an operating aid on the READ side only and changes nothing about
 * how the rows are stored or created — the create form still pre-fills the provision type and lets
 * the user pick the component.
 *
 * "Alle Provisionsarten" (`null`) is the default, so the tab still opens on the complete rule set.
 *
 * ## Why this family exists at all, and why the tab explains itself
 * Two properties of K-18 invert what a caseworker has learned from the four sibling tables, so both
 * are stated above the table instead of left to be discovered:
 *
 *   1. ABSENCE MEANS ELIGIBLE (BR-2). K-05 `ProvisionTypeContractEligibility` needs a matching row
 *      to make a contract relevant; K-18 needs a matching row to make a component IRRELEVANT. A
 *      component with no K-18 row at all is eligible. This is the deliberate inverse of the K-05
 *      rule and the reason the user refused to merge the two families ("Das macht den UseCase der
 *      Tabelle wässrig!").
 *   2. EVALUATION IS PER FORMER CONTRACT (01-requirement.md §12.7, BR-17..BR-20). Every other
 *      assignment family yields one verdict per contract; K-18 yields one verdict per former
 *      contract of the contract, and the component applies as soon as ONE former contract is
 *      eligible. Dimensions 12 and 13 (Gesellschaft/Laufzeit des Vorvertrags) therefore refer to
 *      the former contract currently under evaluation, not to the main former-contract FK.
 *
 * Order of evaluation, for the same reason: K-05 first and type-wide, then — only on ELIGIBLE —
 * K-18 per component, then K-06 and K-10 (BR-1).
 *
 * ## Widget identity across filter changes (CONFIG-011)
 * Switching the pre-filter must not look like a different table: the child component keeps its
 * alias `config.cp.ProvisionTypeComponentEligibility.table` for every filter value, so column
 * widths and sorting survive; the column set is fixed, so it does not shift while the user pages
 * through components.
 */
@Component({
    selector: 'cp-provision-type-component-eligibility',
    templateUrl: './cp-provision-type-component-eligibility.component.html',
    styleUrls: ['./cp-provision-type-component-eligibility.component.scss'],
    standalone: false
})
export class CpProvisionTypeComponentEligibilityComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Descriptor of the component-eligibility child list, from `CpProvisionTypeBaseComponent`. */
    @Input() child: CpConfigChildTab;

    /** "Im Bedingungssatz öffnen" of the condition preview, forwarded to the object view. */
    @Output() onJumpToConditionSet = new EventEmitter<number>();

    /** Commission components of this provision type — the options of the pre-filter. */
    componentOptions: CpProvisionTypeComponentOption[] = [];

    /** Selected commission component; `null` = no pre-filter, the complete rule set is shown. */
    selectedComponentId: number | null = null;

    /** Handed to the child table; `null` while nothing is pre-filtered. */
    additionalFilters: FilterCriteria[] = null;

    private componentSubscription: Subscription;

    constructor(private lookupService: CpProvisionTypeLookupService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.cancelComponentLoad();

        this.selectedComponentId = null;
        this.additionalFilters = null;
        this.componentOptions = [];

        if (!this.provisionTypeId) {
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.componentSubscription = this.lookupService
            .getComponents(this.provisionTypeId)
            .subscribe(options => {
                this.componentOptions = options ?? [];
                this.busy = false;
                this.initialized = true;
            });

        this.initialized = true;
    }

    /**
     * Apply the pre-filter. `null` clears it and returns the complete rule set of the provision
     * type — the state the tab opens in.
     */
    onComponentChange(componentId: number | null): void {
        this.selectedComponentId = componentId ?? null;
        this.additionalFilters = this.selectedComponentId
            ? [FilterCriteria.create(
                'e.provisionTypeComponentId', FilterCriteria.cOperatorEqual, this.selectedComponentId)]
            : null;
    }

    private cancelComponentLoad(): void {
        if (this.componentSubscription) {
            this.componentSubscription.unsubscribe();
            this.componentSubscription = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId'] || changes['child']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.cancelComponentLoad();
    }

}
